import { CheckCircle2, Clock, Flame, Inbox, UserRound } from "lucide-react";

import { getAuthUser } from "@/lib/api/token-store";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

export const TASK_SMART_VIEWS = [
  {
    id: "unclaimed",
    label: "Unclaimed queue",
    icon: Inbox,
    iconClassName: "text-indigo-600 dark:text-indigo-400",
    countKey: "unclaimed",
  },
  {
    id: "mine",
    label: "Assigned to me",
    icon: UserRound,
    iconClassName: "text-indigo-600 dark:text-indigo-400",
    countKey: "mine",
  },
  {
    id: "overdue",
    label: "Overdue",
    icon: Flame,
    iconClassName: "text-rose-600 dark:text-rose-400",
    countKey: "overdue",
    roseBadge: true,
  },
  {
    id: "snoozed",
    label: "Snoozed",
    icon: Clock,
    iconClassName: "text-amber-600 dark:text-amber-400",
    countKey: "snoozed",
  },
  {
    id: "doneToday",
    label: "Done today",
    icon: CheckCircle2,
    iconClassName: "text-emerald-600 dark:text-emerald-400",
    countKey: "doneToday",
  },
];

function sidebarHeading(children) {
  return (
    <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

function teammateInitials(name) {
  const tokens = String(name || "")
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0][0]?.toUpperCase() || "?";
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

/**
 * The Tasks page's left rail (design 04-tasks-page): smart views with counts and per-teammate
 * open-task counts. `selection` is { kind: "view"|"teammate", id } and clicking a row replaces it.
 */
export function SmartViewsSidebar({ counts, selection, onSelect }) {
  const me = String(getAuthUser()?.id || "").trim();
  const teammates = Array.isArray(counts?.teammates) ? counts.teammates : [];

  const isActive = (kind, id) => selection?.kind === kind && selection?.id === id;
  const rowClass = (active) =>
    cn(
      "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-xs font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      active
        ? "bg-card text-foreground shadow-sm ring-1 ring-border"
        : "text-muted-foreground hover:bg-muted hover:text-foreground",
    );

  return (
    <aside className="flex w-[232px] shrink-0 flex-col border-r border-border bg-muted/40 dark:bg-muted/20">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <h1 className="text-[15px] font-bold text-foreground">Tasks</h1>
        <p className="text-[11px] text-muted-foreground">Every follow-up across the clinic</p>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-0.5 p-2.5">
        {sidebarHeading("Smart views")}
        {TASK_SMART_VIEWS.map((view) => {
          const Icon = view.icon;
          const active = isActive("view", view.id);
          const count = Number(counts?.[view.countKey]) || 0;
          return (
            <button
              key={view.id}
              type="button"
              onClick={() => onSelect?.({ kind: "view", id: view.id })}
              className={rowClass(active)}
              aria-current={active ? "true" : undefined}
            >
              <Icon className={cn("h-3.5 w-3.5 shrink-0", view.iconClassName)} strokeWidth={2.25} aria-hidden />
              <span className="min-w-0 flex-1 truncate">{view.label}</span>
              {count > 0 ? (
                view.roseBadge ? (
                  <span className="shrink-0 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold leading-none text-rose-600 dark:bg-rose-500/20 dark:text-rose-300">
                    {count}
                  </span>
                ) : (
                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground/80">{count}</span>
                )
              ) : null}
            </button>
          );
        })}

        {teammates.length > 0 ? (
          <>
            <div className="pt-2">{sidebarHeading("People")}</div>
            {teammates.map((teammate) => {
              const active = isActive("teammate", teammate.id);
              const mine = me && teammate.id === me;
              return (
                <button
                  key={teammate.id}
                  type="button"
                  onClick={() =>
                    onSelect?.({ kind: "teammate", id: teammate.id, name: teammate.displayName })
                  }
                  className={rowClass(active)}
                  aria-current={active ? "true" : undefined}
                >
                  <span
                    className={cn(
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold leading-none",
                      mine ? "bg-indigo-600 text-white dark:bg-indigo-500" : "bg-muted text-muted-foreground",
                    )}
                    aria-hidden
                  >
                    {teammateInitials(teammate.displayName)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {teammate.displayName}
                    {mine ? " (You)" : ""}
                  </span>
                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground/80">
                    {teammate.count}
                  </span>
                </button>
              );
            })}
          </>
        ) : null}
        </div>
      </ScrollArea>
    </aside>
  );
}
